"use client";

import React from "react";
import { motion } from "framer-motion";

const cormorant = {
    className: "font-cormorant font-serif",
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export interface TimelinePhase {
    phase: string;
    title: string;
    range: string;
    description: string;
}

export interface GuestiqoTimelineBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    phases?: TimelinePhase[];
}

export default function GuestiqoTimelineBlock({
    tag = "// Project Chronology",
    title = "8 Months to Production",
    description = "How we designed, verified, and shipped the multi-tenant system in weekly sprint deliveries.",
    phases = [
        { phase: "01", title: "Discovery & Strategy", range: "Month 1 (Weeks 1–4)", description: "Competitive analysis of short-term rental portals, 24 host discovery interviews, and defining multi-tenant DNS routing paths." },
        { phase: "02", title: "Brand Identity", range: "Month 2 (Weeks 5–8)", description: "Visual brand systems, custom logo, warm editorial palette pairing (Inter + Cormorant serif), guidebook print mockups, and QR standee specs." },
        { phase: "03", title: "UX Prototyping", range: "Months 3-4 (Weeks 9–16)", description: "High-fidelity wireframing for 36 responsive views, client preview clickable paths, user tests, and WCAG AA accessibility compliance verification." },
        { phase: "04", title: "Frontend Engineering", range: "Months 5-6 (Weeks 17–24)", description: "Next.js 15 routing configs, streaming pages with Edge execution, and building a modular component library containing 60+ modular blocks." },
        { phase: "05", title: "Tenant Infrastructure", range: "Month 7 (Weeks 25–28)", description: "Firestore schema indices, cloud functions for user subdomain routing, Stripe tenant sub billing pipelines, and Cloudflare routing calls." },
        { phase: "06", title: "QA & Production Launch", range: "Month 8 (Weeks 29–32)", description: "System testing, Cypress integration checks, Lighthouse speed budget compliance review, closed beta testing with 5 hosts, and public release." }
    ]
}: GuestiqoTimelineBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FDFBF7] w-full" data-header-theme="dark">
            <div className="container mx-auto">
                <div className="max-w-3xl mb-20">
                    {tag && <span className="text-xs font-mono text-[#8C8276] uppercase tracking-widest block mb-4">{tag}</span>}
                    {title && <h2 className={`${cormorant.className} text-4xl md:text-6xl font-light text-[#1C1C1C] leading-tight`}>{title}</h2>}
                    {description && (
                        <p className="text-[#5E5E5E] text-sm mt-3 font-sans font-light max-w-xl">
                            {description}
                        </p>
                    )}
                </div>

                {/* Timeline List */}
                <div className="space-y-0 border-t border-[#1C1C1C]/10">
                    {phases.map((time, i) => (
                        <motion.div 
                            key={i}
                            {...fadeUp}
                            transition={{ delay: i * 0.08 }}
                            className="grid grid-cols-[50px_1fr] md:grid-cols-[100px_220px_1fr] gap-4 md:gap-8 py-8 border-b border-[#1C1C1C]/10 hover:bg-[#FAF7F2] transition-colors duration-300 px-4 -mx-4"
                        >
                            <span className={`${cormorant.className} text-3xl font-light text-[#8C8276]/30`}>{time.phase}</span>
                            <div>
                                <h3 className={`${cormorant.className} text-xl md:text-2xl text-[#1C1C1C] font-semibold leading-tight`}>{time.title}</h3>
                                <span className="text-xs text-[#8C8276] font-mono mt-1 block">{time.range}</span>
                            </div>
                            <p className="text-[#5E5E5E] text-xs md:text-sm leading-relaxed font-sans font-light col-span-2 md:col-span-1 mt-2 md:mt-0 max-w-xl">
                                {time.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
